<script setup>
import { computed, ref, watch, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import { useToggle } from '@vueuse/core';
import { vOnClickOutside } from '@vueuse/components';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';

const props = defineProps({
  show: { type: Boolean, default: false },
  conversationIds: { type: Array, default: () => [] },
});

const emit = defineEmits(['close', 'sent', 'update:show']);

const store = useStore();
const { t } = useI18n();

const content = ref('');
const isSubmitting = ref(false);
const cannedContainerRef = useTemplateRef('cannedContainerRef');
const [showCannedMenu, toggleCannedMenu] = useToggle(false);

const cannedResponses = useMapGetter('getCannedResponses');

const localShow = computed({
  get: () => props.show,
  set: value => emit('update:show', value),
});

const canSend = computed(
  () =>
    Boolean(content.value.trim()) &&
    props.conversationIds.length > 0 &&
    !isSubmitting.value
);

const cannedMenuItems = computed(() =>
  (cannedResponses.value || []).map(item => ({
    action: 'select',
    value: item.id,
    label: `/${item.short_code} · ${item.content}`,
    content: item.content,
  }))
);

const resetForm = () => {
  content.value = '';
  isSubmitting.value = false;
  toggleCannedMenu(false);
};

const onClose = () => {
  resetForm();
  emit('update:show', false);
  emit('close');
};

const fetchCannedResponses = () => {
  store.dispatch('getCannedResponse');
};

const applyCannedResponse = item => {
  if (!item?.content) return;
  content.value = item.content;
  toggleCannedMenu(false);
};

const send = async () => {
  if (!canSend.value) return;

  isSubmitting.value = true;
  try {
    const response = await store.dispatch('sendBulkReply', {
      conversation_ids: props.conversationIds,
      content: content.value.trim(),
    });
    const results = response?.data?.data || [];
    const allFailed =
      results.length > 0 && results.every(result => result.status === 'failed');
    if (allFailed) {
      useAlert(t('BULK_ACTION.REPLY.FAILED'));
      return;
    }

    store.dispatch('bulkActions/clearSelectedConversationIds');
    useAlert(t('BULK_ACTION.REPLY.SUCCESFUL'));
    emit('sent');
    onClose();
  } catch (error) {
    useAlert(t('BULK_ACTION.REPLY.FAILED'));
  } finally {
    isSubmitting.value = false;
  }
};

watch(
  () => props.show,
  visible => {
    if (visible) {
      fetchCannedResponses();
    } else {
      resetForm();
    }
  }
);
</script>

<template>
  <woot-modal v-model:show="localShow" :on-close="onClose">
    <div class="flex flex-col">
      <woot-modal-header
        :header-title="$t('BULK_ACTION.REPLY.TITLE')"
        :header-content="
          $t('BULK_ACTION.REPLY.DESC', {
            conversationCount: conversationIds.length,
          })
        "
      />
      <form class="w-full px-5 pb-6" @submit.prevent="send">
        <div class="flex items-center justify-between gap-2 mb-2">
          <span class="text-sm text-n-slate-12">
            {{ $t('BULK_ACTION.REPLY.CONTENT_LABEL') }}
          </span>
          <div ref="cannedContainerRef" class="relative">
            <NextButton
              type="button"
              ghost
              slate
              xs
              icon="i-lucide-zap"
              :label="$t('BULK_ACTION.REPLY.CANNED_RESPONSES')"
              :disabled="isSubmitting"
              :class="{ 'bg-n-alpha-2': showCannedMenu }"
              @click="toggleCannedMenu()"
            />
            <Transition
              enter-active-class="transition-all duration-150 ease-out origin-top"
              enter-from-class="opacity-0 scale-95"
              enter-to-class="opacity-100 scale-100"
              leave-active-class="transition-all duration-100 ease-in origin-top"
              leave-from-class="opacity-100 scale-100"
              leave-to-class="opacity-0 scale-95"
            >
              <DropdownMenu
                v-if="showCannedMenu"
                v-on-click-outside="[
                  () => toggleCannedMenu(false),
                  { ignore: [cannedContainerRef] },
                ]"
                :menu-items="cannedMenuItems"
                :empty-state-message="$t('BULK_ACTION.REPLY.CANNED_EMPTY')"
                show-search
                :search-placeholder="$t('BULK_ACTION.SEARCH_INPUT_PLACEHOLDER')"
                class="right-0 top-9 w-72 max-h-72"
                @action="applyCannedResponse"
              />
            </Transition>
          </div>
        </div>
        <TextArea
          v-model="content"
          :placeholder="$t('BULK_ACTION.REPLY.CONTENT_PLACEHOLDER')"
          :disabled="isSubmitting"
          auto-height
          min-height="8rem"
          max-height="16rem"
        />
        <div class="flex flex-row justify-end w-full gap-2 pt-4">
          <NextButton
            faded
            slate
            type="button"
            :label="$t('BULK_ACTION.REPLY.CANCEL')"
            :disabled="isSubmitting"
            @click="onClose"
          />
          <NextButton
            type="submit"
            :label="$t('BULK_ACTION.REPLY.SEND')"
            :disabled="!canSend"
            :is-loading="isSubmitting"
          />
        </div>
      </form>
    </div>
  </woot-modal>
</template>
